import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font, Image, Link } from '@react-pdf/renderer';

// Font: Giữ nguyên
Font.register({
  family: 'Roboto',
  fonts: [
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf' },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf', fontWeight: 'bold' },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-italic-webfont.ttf', fontStyle: 'italic' }
  ]
});

// Styles: Cập nhật font size và thêm background color
const styles = StyleSheet.create({
  page: { padding: 30, fontFamily: 'Roboto', fontSize: 11, color: '#333' }, // Tăng font size lên 11
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15, borderBottom: '2px solid #007373', paddingBottom: 10 },
  logoContainer: { flexDirection: 'row', alignItems: 'center' },
  logoImage: { width: 50, height: 50, marginRight: 10 }, 
  logoText: { fontSize: 22, fontWeight: 'bold', color: '#007373' },
  bookingNum: { textAlign: 'right' },
  bookingNumLabel: { fontSize: 9, color: '#666' },
  bookingNumValue: { fontSize: 14, fontWeight: 'bold', color: '#007373' },
  
  title: { fontSize: 18, fontWeight: 'bold', marginBottom: 5, color: '#2c3e50', textTransform: 'uppercase' },
  greeting: { marginBottom: 3, fontSize: 11 },
  intro: { marginBottom: 15, fontStyle: 'italic', color: '#555', fontSize: 10 },
  
  // Style cho Booking Details (Nền cam nhạt)
  bookingDetailsContainer: {
    backgroundColor: '#fcecdbff', // Màu cam nhạt
    padding: 10,
    borderRadius: 5,
    marginBottom: 15
  },
  sectionTitle: { fontSize: 13, fontWeight: 'bold', marginBottom: 8, color: '#007373', borderBottom: '1px solid #ddd', paddingBottom: 2 },
  
  row: { flexDirection: 'row', marginBottom: 5, paddingBottom: 2 },
  label: { width: '35%', fontWeight: 'bold', color: '#444' },
  value: { width: '65%', color: '#000' },
  highlight: { color: '#d32f2f', fontWeight: 'bold' },

  // Style cho Policy (Giống ảnh)
  policyContainer: { marginTop: 10, backgroundColor: '#f9f9f9', padding: 10, borderRadius: 4 },
  policyItem: { marginBottom: 10 },
  policyHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 3 },
  policyIcon: { width: 12, height: 12, marginRight: 5 }, // Giả lập icon bằng Text hoặc Image nhỏ nếu có
  policyTitle: { fontWeight: 'bold', fontSize: 11, color: '#000' },
  policyTitleLink: { fontWeight: 'bold', fontSize: 11, color: '#007373', textDecoration: 'none' },
  policyText: { lineHeight: 1.4, color: '#444', fontSize: 10 },
  link: { color: '#007373', textDecoration: 'none', fontWeight: 'bold' },

  // Style cho Footer (Nền nổi bật)
  footerContainer: {
    marginTop: 15,
    backgroundColor: '#E0F2F1', // Màu xanh nhạt (Teal light)
    padding: 10,
    borderRadius: 5,
    textAlign: 'center',
    border: '1px solid #007373'
  },
  footerText: { fontSize: 9, color: '#555', marginTop: 3 },
  footerTitle: { fontSize: 11, fontWeight: 'bold', color: '#007373', marginBottom: 3 }
});

const BookingTicketPdf = ({ data }) => {
  if (!data) return null;

  const logoUrl = typeof window !== 'undefined' ? `${window.location.origin}/fola_logo.png` : '';
  
  // Sử dụng dữ liệu _display được truyền từ Dialog
  const customerName = data.customer_name_display || 'Valued Guest';
  const tourName = data.tour_name_display || '---';
  const startDate = data.start_date_display || '---';
  const addressInfo = data.address_display || '---';
  const totalPrice = data.total_price_display || '0';
  const paymentStatusText = data.payment_status_text || '---';

  const transportInfo = data.transfer_display || 'No';

  const extrasInfo = data.extras && data.extras.length > 0 
    ? data.extras.map(e => `${e.title} (x${e.quantity})`).join(', ')
    : 'N/A';

  return (
    <Document>
      <Page size="A4" style={[styles.page, { lineHeight: data.lineHeight || 1.4 }]}>
        {/* HEADER */}
        <View style={styles.header}>
          <View style={styles.logoContainer}>
             {logoUrl && <Image style={styles.logoImage} src={logoUrl} />}
             <Text style={styles.logoText}>FOLA TRAVEL</Text>
          </View>
          <View style={styles.bookingNum}>
            <Text style={styles.bookingNumLabel}>Booking Number:</Text>
            <Text style={styles.bookingNumValue}>#{data.booking_code}</Text>
          </View>
        </View>

        {/* GREETING */}
        <Text style={styles.title}>Booking Confirmation</Text>
        <Text style={styles.greeting}>Dear {customerName},</Text>
        <Text style={styles.intro}>Thank you for choosing Fola Travel! We are delighted to confirm your booking. Below are the details of your itinerary:</Text>

        {/* BOOKING DETAILS - NỀN CAM NHẠT */}
        <View style={styles.bookingDetailsContainer}>
          <Text style={styles.sectionTitle}>Booking Details</Text>
          
          <View style={styles.row}><Text style={styles.label}>Tour Name:</Text><Text style={styles.value}>{tourName}</Text></View>
          <View style={styles.row}><Text style={styles.label}>Travel Date:</Text><Text style={styles.value}>{startDate}</Text></View>
          <View style={styles.row}><Text style={styles.label}>Lead Traveler:</Text><Text style={styles.value}>{customerName}</Text></View>
          <View style={styles.row}>
            <Text style={styles.label}>Travelers:</Text>
            <Text style={styles.value}>{data.adults_count} Adults, {data.children_count} Children</Text>
          </View>
          <View style={styles.row}><Text style={styles.label}>Transfer:</Text><Text style={styles.value}>{transportInfo}</Text></View>
          <View style={styles.row}><Text style={styles.label}>Address:</Text><Text style={styles.value}>{addressInfo}</Text></View>
          <View style={styles.row}><Text style={styles.label}>Extra Services:</Text><Text style={styles.value}>{extrasInfo}</Text></View>
          <View style={styles.row}><Text style={styles.label}>Special Requests:</Text><Text style={styles.value}>{data.special_request || 'None'}</Text></View>
          
          <View style={styles.row}>
             <Text style={styles.label}>Total Amount:</Text>
             <Text style={[styles.value, styles.highlight]}>{totalPrice} VND</Text>
          </View>
          
          <View style={styles.row}>
             <Text style={styles.label}>Payment Status:</Text>
             <Text style={styles.value}>{paymentStatusText}</Text>
          </View>
        </View>

        {/* POLICY SECTION */}
        <Text style={styles.sectionTitle}>Payment & Cancellation Policy</Text>
        <View style={styles.policyContainer}>
          {/* Payment */}
          <View style={styles.policyItem}>
            <View style={styles.policyHeader}>
               <Link src="https://folatravel.com/payment-method/" style={styles.policyTitleLink}>Payment</Link>
            </View>
            <Text style={styles.policyText}>
              We accept payments in cash, bank transfer, or via payment gateways such as PayPal or Onepay.
            </Text>
          </View>

          {/* Cancellation */}
          <View style={styles.policyItem}>
             <View style={styles.policyHeader}>
               <Link src="https://folatravel.com/term-and-conditions/" style={styles.policyTitleLink}>Cancellation Policy</Link>
            </View>
            <Text style={styles.policyText}>
              We offer flexible cancellation policies with free cancellations up to 3 days before the departure date.
            </Text>
          </View>
        </View>

        {/* FOOTER - NỀN NỔI BẬT */}
        <View style={styles.footerContainer}>
           <Text style={styles.footerTitle}>Need help? Contact us:</Text>
           <Text style={styles.footerText}>Hotline/Whatsapp: (+84) 986.332.366  -  Email: folatravel@gmail.com</Text>
           <Text style={styles.footerText}>Fola Travel and Services Co.Ltd, Pavilion P2, Vinhomes Ocean Park, Gia Lam, Hanoi.</Text>
           <Text style={{...styles.footerText, marginTop: 5}}>© 2021-{new Date().getFullYear()} Fola Travel. All rights reserved.</Text>
        </View>
      </Page>
    </Document>
  );
};

export default BookingTicketPdf;